/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { message } from 'antd';
import fa from 'locales/fa.json';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CourseRoute, LessonRoute, LessonUrl, PassedUrl } from 'services/routes';
import { CourseType, LessonType } from 'types/course.type';
import LessonsList from 'components/Common/LessonsList';
import 'plyr-react/dist/plyr.css';
import LessonTabs from './lessonTabs';
import request from 'services/request';
import { PLAYER_CONTROLS } from 'utils/common.util';
import LoadingBox from 'components/Common/LoadingBox';
import { useDispatch, useSelector } from 'react-redux';
import { getChapterAction } from 'store/course/course.action';
import Plyr from 'plyr-react';
import styled from '@emotion/styled';
import { UPDATE_USER } from 'store/account/account.constants';
import Image from 'next/image';
import LessonTitle from './lessonTitle';
import LessonNavigation from './lessonNavigation';
import LessonActions from './lessonActions';
import SimpleSwitch from 'components/Common/SimpleSwitch';

export const SPlyr = styled.div`
  border-radius: 12px;
  .plyr {
    min-width: 150px;
  }
  .plyr-react {
    display: none;
  }
`;

type LessonPageType = { course: CourseType; lesson: LessonType };
const Lesson: React.FC<LessonPageType> = ({ course, lesson }) => {
  const router = useRouter();
  const ref = useRef(null);
  const dispatch = useDispatch();
  const { courseId, lessonId } = router.query;
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);
  const [audio, setAudio] = useState(null);
  const [film, setFilm] = useState(null);
  const [typeIndex, setTypeIndex] = useState(0);
  const file = typeIndex === 1 ? audio : film;
  const type = file?.includes('mp4') ? 'video' : 'audio';
  const user = useSelector((state) => state.account.user);
  const passed = user?.workshops
    ?.find((key) => String(key.id) === courseId)
    ?.passed_lessons.includes(Number(lessonId));
  const courseError = useSelector((state) => state.course.chapters)?.[courseId as string]
    ?.error;

  const getData = async (): Promise<void> => {
    const res = await request.get(LessonUrl(courseId, lessonId));
    res.ok ? setData(res.data) : setError(true);
  };

  useEffect(() => {
    localStorage?.removeItem('termId'); // to not redirect to term-lesson if exist
    if (lessonId) {
      setData(null);
      getData();
    }
  }, [lessonId, course]);

  useEffect(() => {
    if (data) {
      const filmFile = data?.files.find((item) =>
        item.file.toLowerCase().includes('.mp4'),
      );
      const audioFile = data?.files?.find((item) =>
        item.file.toLowerCase().includes('mp3'),
      );
      setAudio(audioFile?.file);
      setFilm(filmFile?.file);
      !filmFile?.file && setTypeIndex(1);
      !audioFile?.file && setTypeIndex(0);
    }
    if (data?.exam?.passed && !data?.passed && course?.registered) {
      request.post(PassedUrl(courseId, lessonId));
      dispatch({
        type: UPDATE_USER,
        filed: 'passed_lessons',
        lessonId,
        courseId,
      });
    }
  }, [data]);

  const onLesson = (id): void => {
    const target = course.chapters
      ?.map((item) => item.lessons.map((k) => k))
      .flat()
      .find((item) => item.id === Number(id));

    target.files[0].file
      ? router.push(LessonRoute(course.id, id, target.title))
      : message.warning(fa.course.notAllow);
  };

  const reloadData = (): void => {
    dispatch(getChapterAction(courseId));
  };

  const plyrPlayer = useMemo(
    () => (
      <SPlyr>
        <Plyr
          ref={(player: any): void => (ref.current = player)} // eslint-disable-line
          source={{ type, sources: [{ src: file }] }}
          options={{ controls: PLAYER_CONTROLS }}
        />
      </SPlyr>
    ),
    [file],
  );

  return (
    <div className="duration-300 bg-gray-0 min-h-[calc(100vh-70px)] flex-col flex items-center justify-items-start">
      <div className="xl:pr-[370px] w-screen mt-[20px] md:mt-0 md:w-[700px] py-[20px] xl:pl-[20px]  xl:justify-self-start xl:w-full">
        <div className="w-full bg-white rounded-[8px] min-h-[600px]">
          <LoadingBox data={course} error={courseError} reload={reloadData}>
            <LessonTitle courseTitle={course?.title} lessonTitle={lesson?.title} />

            <div className="relative">
              {audio && film && (
                <div className="absolute left-[20px] top-[18px] z-[1]">
                  <SimpleSwitch
                    items={[fa.global.film, fa.global.audio]}
                    setActiveIndex={setTypeIndex}
                    activeIndex={typeIndex}
                  />
                </div>
              )}
              {(type === 'audio' || typeIndex === 1) && (
                <Image
                  src={course?.image}
                  width={265}
                  height={150}
                  alt=""
                  layout="responsive"
                />
              )}

              {plyrPlayer}
            </div>

            <LessonTabs
              data={data}
              registered={course?.registered}
              error={error}
              player={ref}
              reload={getData}
            />
          </LoadingBox>
        </div>
        <div className="bg-white mt-10 p-8 rounded-lg">
          <div className="font-bold text-22 mb-8">توضیحات</div>
          <div className="">
            {lesson?.description.split('\r\n').map((item, index) => (
              <div className="my-[5px]" key={index}>
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="w-screen mb-[20px]  rounded-[8px] xl:rounded mt-[170px] xl:mt-0 xl:mb-0 h-[470px] md:w-[700px] xl:pb-[23rem] relative xl:fixed right-0 bg-white xl:w-[350px] xl:h-[calc(100%-70px)]">
        <LoadingBox data={course} error={courseError} reload={reloadData}>
          <LessonNavigation
            nextLesson={lesson?.next_lesson}
            previousLesson={lesson?.previous_lesson}
            onLesson={onLesson}
            mainHref={CourseRoute(course?.id, course?.title)}
          />

          <div className="overflow-auto h-[400px] xl:h-[100%] toRight">
            <LessonsList course={course} activeId={lesson?.id} />
          </div>

          <LessonActions
            data={data}
            registered={course?.registered}
            film={film}
            audio={audio}
            passed={passed}
          />
        </LoadingBox>
      </div>
    </div>
  );
};

export default Lesson;
